// Recipes the harness can run for this user: what was distilled from runs
// that ended in supported claims, with how often each has been run and how
// often it delivered. Nothing is seeded; every recipe is learned, so every
// recipe is subject to the same gate. Browser-local, like the investigation
// graph and the harness lessons, and gated the same way: a confidence that
// decays weekly unless a recipe keeps delivering, an archive threshold,
// retirement after consecutive failures, and retirement when the schema no
// longer serves a recipe's entity.
//
// Beneath the browser's own recipes sits the org's: the recipes other chats
// kept, read from the trace store into a cache (orgRecipes.js). They are
// listed, run and gated like the local ones, with this browser's own usage
// record; a local recipe shadows an org recipe with the same id or the same
// plan, and a plan this browser learns that the org already holds is
// adopted under the org's id rather than stored twice.
import { browserStorageKey } from "./browserStorageScope.js";
import { mergeRecipeTemplates, recipeSignature, stepEntity } from "./graphRecipes.js";
import { schemaHash, schemaNames } from "./harnessMemoryCapture.js";
import { createOrgRecipeCache } from "./orgRecipes.js";

// The key moves when what a stored recipe means changes: v1 recipes had an
// origin that counted only the traced graph tools and plans distilled by
// earlier rules, so they would be scored against the wrong cost. Old keys
// are removed on first read.
export const RECIPE_STORAGE_PREFIX = "origin-recipes-v2";
// An opaque id for this browser's copy of the store, minted once, so the
// records it publishes into the trace store supersede each other rather
// than add up, and so it can leave its own out when it reads the org's.
export const BROWSER_ID_PREFIX = "origin-recipes-browser-v1";
const LEGACY_STORAGE_PREFIXES = ["origin-recipes-v1"];
export const MAX_STORED_RECIPES = 20;
export const DECAY_PER_WEEK = 0.9;
export const ARCHIVE_BELOW = 0.2;
export const RETIRE_AFTER_FAILURES = 3;
// How many of a recipe's runs are kept for the savings average.
export const MAX_RUN_HISTORY = 10;
// Where a recipe starts before its record counts, and how many runs that
// prior is worth against the record: two, so one success or failure moves
// it but does not settle it. A freshly distilled recipe already has one
// success, so it enters at (2 * 0.6 + 1) / 3.
export const PRIOR = 0.6;
const PRIOR_WEIGHT = 2;
const WEEK_MS = 7 * 24 * 3600 * 1000;

// Successes over runs, anchored by the prior, then decayed by the weeks
// since the recipe last ran (or was distilled).
// The org's record of the recipe, read back from the trace store, counts
// beside this browser's own: a recipe other browsers have run and seen
// deliver starts trusted here, and one they have seen fail starts low.
export function effectiveConfidence(recipe, now = Date.now()) {
    const uses = (recipe?.uses || 0) + (recipe?.orgUses || 0);
    const successes = (recipe?.successes || 0) + (recipe?.orgSuccesses || 0);
    const base = (PRIOR_WEIGHT * PRIOR + successes) / (PRIOR_WEIGHT + uses);
    const candidates = [recipe?.lastUsedAt, recipe?.orgLastUsedAt, recipe?.createdAt ? Date.parse(recipe.createdAt) : null].filter((t) => Number.isFinite(t) && t > 0);
    const since = candidates.length ? Math.max(...candidates) : null;
    if (!since || !Number.isFinite(since)) return base;
    const weeks = Math.max(0, (now - since) / WEEK_MS);
    return base * Math.pow(DECAY_PER_WEEK, weeks);
}

// A recipe's search entity must exist in the schema the harness is talking
// to. Recipes spell entities without the proto prefix and the schema with
// it. Only the entity is checked: the schema's field inventory covers join
// fields, not every dimension, so a term field's absence there means
// nothing.
function servedBy(names, recipe) {
    if (!names) return true;
    // Every query step's entity must be served: a distilled plan is `query`
    // steps, a hand-written one may use `search`.
    for (const step of recipe.steps || []) {
        if (step.kind !== "search" && step.kind !== "query") continue;
        const entity = stepEntity(step);
        if (!entity) continue;
        if (!names.entities.has(entity) && !names.entities.has(`ENTITY_TYPE_${entity}`)) return false;
    }
    return true;
}

export function createRecipeStore({ storage = null, keyFor = () => browserStorageKey(RECIPE_STORAGE_PREFIX), browserKeyFor = () => browserStorageKey(BROWSER_ID_PREFIX), now = () => Date.now(), org = undefined, randomId = () => (globalThis.crypto?.randomUUID?.() || `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`) } = {}) {
    let knownNames = null;
    // `org: false` is a store with no org layer (tests, focused surfaces).
    const orgCache = org === false ? null : (org || createOrgRecipeCache({ now }));
    function resolveStorage() {
        if (storage) return storage;
        try { return globalThis.localStorage || null; } catch { return null; }
    }
    let legacyCleared = false;
    function clearLegacy(s) {
        if (legacyCleared || !s || typeof s.key !== "function") return;
        legacyCleared = true;
        try {
            const stale = [];
            for (let i = 0; i < (s.length || 0); i++) { const k = s.key(i); if (k && LEGACY_STORAGE_PREFIXES.some((p) => k === p || k.startsWith(`${p}:`))) stale.push(k); }
            for (const k of stale) s.removeItem(k);
        } catch { /* best effort */ }
    }
    function read() {
        const key = keyFor();
        const s = resolveStorage();
        clearLegacy(s);
        // The org's recipes ride on the read, once per operation, so a list
        // reads the cache once however many recipes it holds.
        const org = orgCache ? orgCache.list() : [];
        if (!key || !s) return { recipes: [], usage: {}, schemaHash: null, lastSettle: null, org };
        try {
            const raw = s.getItem(key);
            const data = raw ? JSON.parse(raw) : null;
            return {
                recipes: Array.isArray(data?.recipes) ? data.recipes : [],
                usage: data?.usage && typeof data.usage === "object" ? data.usage : {},
                schemaHash: data?.schemaHash || null,
                lastSettle: data?.lastSettle && typeof data.lastSettle === "object" ? data.lastSettle : null,
                org,
            };
        } catch { return { recipes: [], usage: {}, schemaHash: null, lastSettle: null, org }; }
    }
    // The org's recipes not shadowed by a local one: same id (this browser
    // saved or adopted it) or same plan (learned here independently).
    function orgRecipes(data) {
        const ids = new Set(data.recipes.map((r) => r.id));
        const sigs = new Set(data.recipes.map((r) => recipeSignature(r)));
        return (data.org || []).filter((r) => !ids.has(r.id) && !sigs.has(recipeSignature(r)));
    }
    function allRecipes(data) { return [...data.recipes, ...orgRecipes(data)]; }
    function orgCopyOf(data, recipe) {
        const org = data.org || [];
        const byId = org.find((o) => o.id === recipe.id);
        if (byId) return byId;
        let sig = null;
        try { sig = recipeSignature(recipe); } catch { return null; }
        return org.find((o) => { try { return recipeSignature(o) === sig; } catch { return false; } }) || null;
    }
    // A write that the storage refuses (the origin's quota is shared with
    // the investigation graphs, which are large) is retried once without
    // archived recipes and run histories; the caller learns whether it
    // landed, and says so, instead of a recipe vanishing between "learned"
    // and "listed".
    let lastWriteError = null;
    function write({ org: _org, ...data }) {
        const key = keyFor();
        const s = resolveStorage();
        if (!key || !s) { lastWriteError = "no browser storage"; return false; }
        try { s.setItem(key, JSON.stringify({ version: 2, savedAt: now(), ...data })); lastWriteError = null; return true; } catch (err) {
            try {
                const active = (data.recipes || []).filter((r) => (data.usage?.[r.id]?.status || "active") === "active");
                const usage = Object.fromEntries(active.map((r) => { const u = data.usage?.[r.id] || {}; const { runs, ...rest } = u; return [r.id, rest]; }));
                s.setItem(key, JSON.stringify({ version: 2, savedAt: now(), ...data, recipes: active, usage }));
                lastWriteError = null;
                return true;
            } catch (err2) {
                lastWriteError = `browser storage refused the write (${(err2 || err)?.name || "error"}); the origin's storage is likely full`;
                return false;
            }
        }
    }
    function usageOf(data, id) {
        return data.usage[id] || { uses: 0, successes: 0, streak: 0, lastUsedAt: null, status: "active" };
    }
    function archive(data, id, reason) {
        const u = usageOf(data, id);
        data.usage[id] = { ...u, status: "archived", retiredReason: reason, retiredAt: now() };
    }
    function withUsage(recipe, data) {
        const u = usageOf(data, recipe.id);
        const view = {
            ...recipe,
            examples: Array.isArray(recipe.examples) ? recipe.examples : (recipe.example ? [recipe.example] : []),
            paraphrases: Array.isArray(recipe.paraphrases) ? recipe.paraphrases : [],
            origin: recipe.origin || null,
            // Where the org's copy came from, for an org recipe and for a
            // local one the org's store also holds: by id, or by plan when
            // a newer save of the same plan has become the org's copy.
            org: recipe.org || orgCopyOf(data, recipe)?.org || null,
            uses: u.uses || 0,
            successes: u.successes || 0,
            streak: u.streak || 0,
            lastUsedAt: u.lastUsedAt || null,
            status: u.status || "active",
            retiredReason: u.retiredReason || null,
            savedAvg: Number.isFinite(u.savedAvg) ? u.savedAvg : null,
            runsRecorded: Array.isArray(u.runs) ? u.runs.length : 0,
            lastRun: Array.isArray(u.runs) && u.runs.length ? { success: Boolean(u.runs[u.runs.length - 1].success), basis: u.runs[u.runs.length - 1].basis || null, reason: u.runs[u.runs.length - 1].reason || null, saved: u.runs[u.runs.length - 1].saved ?? null } : null,
        };
        // What other browsers recorded of this recipe, read back from the
        // trace store; this browser's own records are left out of the read.
        const record = view.org?.record || null;
        view.orgUses = record?.uses || 0;
        view.orgSuccesses = record?.successes || 0;
        view.orgSavedAvg = Number.isFinite(record?.savedAvg) ? record.savedAvg : null;
        view.orgBrowsers = record?.browsers || 0;
        view.orgLastUsedAt = record?.lastUsedAt || null;
        view.confidence = effectiveConfidence(view, now());
        view.stale = !servedBy(knownNames, recipe);
        return view;
    }
    // Decayed recipes leave the active set on read, so the list the prompt
    // sees is always current. A recipe with no origin cost was distilled
    // before runs were judged by what they save; it cannot be scored, so it
    // retires as legacy rather than firing on a matching question forever.
    function sweep(data) {
        let changed = false;
        for (const r of allRecipes(data)) {
            const view = withUsage(r, data);
            if (view.status !== "active") continue;
            if (!Number.isFinite(r.origin?.calls)) { archive(data, r.id, "legacy"); changed = true; continue; }
            if (view.confidence < ARCHIVE_BELOW) { archive(data, r.id, "decayed"); changed = true; }
        }
        if (changed) write(data);
        return data;
    }
    function rank(a, b) {
        return (b.confidence - a.confidence) || (b.successes - a.successes) || String(b.createdAt || "").localeCompare(String(a.createdAt || ""));
    }
    return {
        list({ includeArchived = false } = {}) {
            const data = sweep(read());
            const all = allRecipes(data).map((r) => withUsage(r, data));
            return all.filter((r) => includeArchived || r.status === "active").sort(rank);
        },
        get(id) {
            return this.list({ includeArchived: true }).find((r) => r.id === id) || null;
        },
        // Same search entity and anchor kind means the same recipe: the
        // existing one keeps its id and gains a success; an archived one
        // that delivers again comes back.
        upsert(recipe) {
            if (!recipe || !recipe.id || !Array.isArray(recipe.steps)) return { added: false, id: null };
            const data = read();
            const sig = recipeSignature(recipe);
            const credit = (id) => {
                const u = usageOf(data, id);
                data.usage[id] = { ...u, uses: (u.uses || 0) + 1, successes: (u.successes || 0) + 1, streak: 0, lastUsedAt: now(), status: "active", retiredReason: null };
            };
            const existing = data.recipes.find((r) => recipeSignature(r) === sig);
            if (existing) {
                // The same path again: the stored plan generalizes where the
                // new run parameterized a literal, and learns the new question.
                const { recipe: merged, generalized, newExample } = mergeRecipeTemplates(existing, recipe);
                data.recipes = data.recipes.map((r) => (r.id === existing.id ? { ...merged, id: existing.id } : r));
                credit(existing.id);
                if (!write(data)) return { added: false, id: existing.id, matched: "stored", error: lastWriteError || "browser storage refused the write" };
                return { added: false, id: existing.id, matched: "stored", generalized, newExample };
            }
            // The org already holds this plan: adopt it under the org's id,
            // generalized by this run, so the org counts one recipe and this
            // browser's record of it starts with this success.
            const adoptable = orgRecipes(data).find((r) => recipeSignature(r) === sig);
            if (adoptable) {
                const { recipe: merged, generalized, newExample } = mergeRecipeTemplates(adoptable, recipe);
                const { org: _org, ...plain } = merged;
                data.recipes.push({ ...plain, id: adoptable.id, createdAt: adoptable.createdAt || new Date(now()).toISOString() });
                credit(adoptable.id);
                if (!write(data)) return { added: false, id: adoptable.id, matched: "org", error: lastWriteError || "browser storage refused the write" };
                return { added: false, id: adoptable.id, matched: "org", generalized, newExample };
            }
            const stored = { ...recipe, createdAt: new Date(now()).toISOString() };
            data.recipes.push(stored);
            data.usage[stored.id] = { uses: 1, successes: 1, streak: 0, lastUsedAt: now(), status: "active" };
            if (data.recipes.length > MAX_STORED_RECIPES) {
                const ranked = data.recipes.map((r) => withUsage(r, data)).sort((a, b) => (a.status === "active") - (b.status === "active") || (a.confidence - b.confidence) || ((a.lastUsedAt || 0) - (b.lastUsedAt || 0)));
                for (const r of ranked.slice(0, data.recipes.length - MAX_STORED_RECIPES)) { data.recipes = data.recipes.filter((x) => x.id !== r.id); delete data.usage[r.id]; }
            }
            if (!write(data)) return { added: false, id: null, error: lastWriteError || "browser storage refused the write" };
            return { added: true, id: stored.id };
        },
        // A run's verdict comes from the settle (graphRecipes.judgeRecipeRun):
        // what the run's claims rested on and whether it came in under the
        // recipe's origin cost. Three failures in a row retire a recipe; a
        // success resets the streak. The run's cost, savings, basis and
        // reason are kept for the average the prompt shows and for /recipes.
        recordUse(id, { success = false, calls = null, saved = null, basis = null, reason = null } = {}) {
            const data = read();
            const u = usageOf(data, id);
            const streak = success ? 0 : (u.streak || 0) + 1;
            const runs = Array.isArray(u.runs) ? u.runs.slice() : [];
            if (Number.isFinite(calls) || Number.isFinite(saved) || basis || reason) runs.push({ calls: Number.isFinite(calls) ? calls : null, saved: Number.isFinite(saved) ? saved : null, success, basis: basis || null, reason: reason || null, at: now() });
            const savings = runs.map((r) => r.saved).filter((v) => Number.isFinite(v));
            const savedAvg = savings.length ? savings.reduce((a, b) => a + b, 0) / savings.length : (Number.isFinite(u.savedAvg) ? u.savedAvg : null);
            data.usage[id] = { ...u, uses: (u.uses || 0) + 1, successes: (u.successes || 0) + (success ? 1 : 0), streak, lastUsedAt: now(), runs: runs.slice(-MAX_RUN_HISTORY), savedAvg };
            if (streak >= RETIRE_AFTER_FAILURES) archive(data, id, "failed");
            return write(data);
        },
        // Replace what the describer writes (name, trigger, parameter
        // descriptions, paraphrases) on a stored recipe.
        describe(id, { name, when, params, paraphrases } = {}) {
            const data = read();
            const r = data.recipes.find((x) => x.id === id);
            if (!r) return false;
            if (typeof name === "string" && name.trim()) r.name = name.trim();
            if (typeof when === "string" && when.trim()) r.when = when.trim();
            if (Array.isArray(params) && params.length) r.params = params;
            if (Array.isArray(paraphrases)) r.paraphrases = paraphrases.slice(0, 3);
            return write(data);
        },
        // Stamp the store with the live schema and retire recipes whose
        // search entity it no longer serves.
        reconcileSchema(schema) {
            const hash = schemaHash(schema);
            if (!hash) return { hash: null, retired: 0 };
            knownNames = schemaNames(schema);
            const data = read();
            let retired = 0;
            for (const r of allRecipes(data)) {
                if (usageOf(data, r.id).status !== "active") continue;
                if (!servedBy(knownNames, r)) { archive(data, r.id, "schema_changed"); retired++; }
            }
            if (retired || hash !== data.schemaHash) write({ ...data, schemaHash: hash });
            return { hash, retired };
        },
        // Forgetting also drops the last-run note when it names what was
        // forgotten, so /recipes never says "last run learned X" over an
        // empty list. An org recipe cannot be removed from the org, so it is
        // hidden on this browser instead, and stays hidden when read again.
        forget(id) {
            const data = read();
            const gone = data.recipes.find((r) => r.id === id);
            data.recipes = data.recipes.filter((r) => r.id !== id);
            const orgHeld = (data.org || []).some((r) => r.id === id);
            if (orgHeld) archive(data, id, "forgotten"); else delete data.usage[id];
            if (gone && data.lastSettle?.learned && data.lastSettle.learned === gone.name) data.lastSettle = null;
            write(data);
            return Boolean(gone) || orgHeld;
        },
        // Every local recipe goes; every org recipe the cache holds is
        // hidden, the shadowed ones too so they do not surface once their
        // local copy is gone. Returns how many recipes were listed in all;
        // org recipes saved later still arrive.
        forgetAll() {
            const data = read();
            const usage = {};
            for (const r of data.org || []) usage[r.id] = { uses: 0, successes: 0, streak: 0, lastUsedAt: null, status: "archived", retiredReason: "forgotten", retiredAt: now() };
            const n = data.recipes.length + orgRecipes(data).length;
            write({ recipes: [], usage, schemaHash: data.schemaHash, lastSettle: null });
            return n;
        },
        // What the last run end did about recipes: learned one (or merged
        // into one), or why not. Shown by /recipes so learning is never
        // silent.
        noteSettle({ learned = null, merged = false, reason = null } = {}) {
            const data = read();
            return write({ ...data, lastSettle: { at: now(), learned, merged, reason } });
        },
        lastSettle() { return read().lastSettle; },
        lastWriteError() { return lastWriteError; },
        stats() {
            const data = read();
            const stored = allRecipes(data).map((r) => withUsage(r, data));
            const active = stored.filter((r) => r.status === "active");
            return {
                stored: stored.length,
                active: active.length,
                archived: stored.filter((r) => r.status !== "active").length,
                runs: Object.values(data.usage).reduce((n, u) => n + (u.uses || 0), 0),
                local: data.recipes.length,
                localActive: active.filter((r) => !r.org || data.recipes.some((l) => l.id === r.id)).length,
                orgActive: active.filter((r) => r.org && !data.recipes.some((l) => l.id === r.id)).length,
                // Org recipes not shadowed by a local one, whatever their status.
                orgListed: orgRecipes(data).length,
                org: orgCache?.available?.() ? orgCache.status() : null,
            };
        },
        effectiveConfidence(recipe) { return effectiveConfidence(recipe, now()); },
        // Minted on first use and kept with the store; null where the browser
        // has no verified identity to keep it under.
        browserId() {
            const key = browserKeyFor();
            const s = resolveStorage();
            if (!key || !s) return null;
            try {
                const existing = s.getItem(key);
                if (existing) return existing;
                const minted = String(randomId());
                s.setItem(key, minted);
                return minted;
            } catch { return null; }
        },
        org: orgCache,
    };
}
